import { useEffect, useRef, useState } from 'react';
import './modal-signature-canvas.css';

interface SignModalProps {
  slot: number | null; // Cambiado a number | null para mayor flexibilidad
  onComplete: (base64: string) => void;
  onCancel: () => void;
}

const MIN_SIGNATURE_LENGTH_PX = 280;

const ModalSignatureCanvas = ({ slot, onComplete, onCancel }: SignModalProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);

  const [isDrawing, setIsDrawing] = useState(false);
  const [strokeLength, setStrokeLength] = useState(0);

  /* =========================
     INIT CANVAS (FIX)
  ========================= */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const setupCanvas = () => {
      const ratio = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();

      canvas.width = rect.width * ratio;
      canvas.height = rect.height * ratio;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.setTransform(1, 0, 0, 1, 0, 0); 
      ctx.scale(ratio, ratio);

      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;

      ctxRef.current = ctx;
    };

    requestAnimationFrame(setupCanvas);
    window.addEventListener('resize', setupCanvas);
    return () => window.removeEventListener('resize', setupCanvas);
  }, []);

  /* ... (getPos, startDraw, draw, endDraw, clearCanvas se mantienen igual) ... */
  const getPos = (e: MouseEvent | TouchEvent) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    if ('touches' in e) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: (e as MouseEvent).clientX - rect.left, y: (e as MouseEvent).clientY - rect.top };
  };

  const startDraw = (e: any) => {
    e.preventDefault();
    const ctx = ctxRef.current;
    if (!ctx) return;
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    lastPointRef.current = { x, y };
    setIsDrawing(true);
  };

  const draw = (e: any) => {
    if (!isDrawing || !ctxRef.current || !lastPointRef.current) return;
    const ctx = ctxRef.current;
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    const dx = x - lastPointRef.current.x;
    const dy = y - lastPointRef.current.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    setStrokeLength(prev => prev + distance);
    lastPointRef.current = { x, y };
  };

  const endDraw = () => { setIsDrawing(false); lastPointRef.current = null; };
  const clearCanvas = () => {
    const canvas = canvasRef.current!;
    const ctx = ctxRef.current!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setStrokeLength(0);
  };

  const confirmSignature = () => {
    if (!isSignatureValid) return;
    const canvas = canvasRef.current!;
    onComplete(canvas.toDataURL('image/png'));
  };

  const isSignatureValid = strokeLength >= MIN_SIGNATURE_LENGTH_PX;

  return (
    <div className="sign-modal-backdrop">
      <div className="sign-modal">
        {/* TITULO DINÁMICO: Si es 1 o 2 mantenemos el texto original, si no, ponemos el número */}
        <h2>
          {slot === 1 ? 'Firma del tutor' : slot === 2 ? 'Firma del paciente' : `Firma ${slot}`}
        </h2>

        <p className="sign-legal-text">
          Firme en el recuadro. Esta firma electrónica tiene validez legal conforme al Reglamento eIDAS.
        </p>

        <canvas
          ref={canvasRef}
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={endDraw}
        />

        <div className="signature-feedback">
          <span className='signature-feedback-text'>
            Longitud de firma: 
            <span className={!isSignatureValid && strokeLength > 0 ? "signature-feedback-warning" : "signature-feedback-text"}>
              {Math.round(strokeLength)}
            </span> px.
          </span>
          <div className="signature-feedback-warning" style={{ minHeight: '18px', marginTop: '4px', visibility: !isSignatureValid && strokeLength > 0 ? 'visible' : 'hidden' }}>
            La firma debe ser más completa para ser válida
          </div>
        </div>

        <div className="sign-modal-actions">
          <button onClick={clearCanvas} className="secondary">Limpiar</button>
          <button onClick={onCancel} className="secondary">Cancelar</button>
          <button onClick={confirmSignature} disabled={!isSignatureValid} className="primary">Confirmar firma</button>
        </div>
      </div>
    </div>
  );
};

export default ModalSignatureCanvas;